import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import SimpleBanner from '../components/SimpleBanner';
import aboutBg from '../assets/About.jpg';
import axios from 'axios';
import { jsPDF } from 'jspdf'; // Import jsPDF

// ReceiptPopup component - now accepts onDownloadPdf prop
const ReceiptPopup = ({ data, onClose, onDownloadPdf }) => {
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    try {
      return new Date(dateString).toLocaleDateString(undefined, options);
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString; // Fallback to original string if invalid date
    }
  };

  return (
    <>
      <style>
        {`
          .popup-backdrop {
            position: fixed;
            inset: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 9999;
            animation: fadeIn 0.3s ease-out; /* Added fade in for backdrop */
          }

          .popup-container {
            background-color: #ffffff;
            padding: 24px;
            border-radius: 12px;
            max-width: 400px;
            width: 90%; /* Make it slightly more responsive */
            position: relative;
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
            animation: fadeInUp 0.3s ease-out;
          }

          .popup-close {
            position: absolute;
            top: 8px;
            right: 12px;
            font-size: 20px;
            font-weight: bold;
            color: #555;
            background: none;
            border: none;
            cursor: pointer;
          }

          .popup-close:hover {
            color: #000;
          }

          .popup-title {
            font-size: 24px;
            font-weight: 600;
            text-align: center;
            color: #1E1E1E;
            margin-bottom: 16px;
          }

          .popup-info p {
            font-size: 14px;
            color: #444;
            margin: 6px 0;
          }

          .popup-buttons {
            margin-top: 20px;
            display: flex;
            flex-direction: column;
            gap: 10px;
          }

          .popup-buttons button {
            padding: 10px 16px;
            border-radius: 6px;
            cursor: pointer;
            font-size: 14px;
            font-weight: 500;
            border: none;
            transition: background-color 0.3s ease;
          }

          .popup-buttons .download {
            background-color: #1E1E1E;
            color: white;
          }

          .popup-buttons .download:hover {
            background-color: black;
          }

          .popup-buttons .close {
            background-color: #f2f2f2;
            color: #333;
          }

          .popup-buttons .close:hover {
            background-color: #e0e0e0;
          }

          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(40px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>

      <div className="popup-backdrop">
        <div className="popup-container">
          <button onClick={onClose} className="popup-close">×</button>
          <h2 className="popup-title">Reservation Confirmed 🎉</h2>
          <div className="popup-info">
            <p><strong>Name:</strong> {data.name}</p>
            <p><strong>Email:</strong> {data.email}</p>
            <p><strong>Phone:</strong> {data.phone}</p>
            <p><strong>Date:</strong> {formatDate(data.date)}</p>
            <p><strong>Time:</strong> {data.time}</p>
            <p><strong>Persons:</strong> {data.persons}</p>
            <p><strong>Status:</strong> {data.status}</p>
          </div>
          <div className="popup-buttons">
            {/* Call onDownloadPdf passing the data and formatDate function */}
            <button className="download" onClick={() => onDownloadPdf(data, formatDate)}>Download Receipt</button>
            <button className="close" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </>
  );
};

const ContactUs = ({ id }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const sectionRef = useRef(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState(null);

  const address = 'The Porch Cafe & Restaurant, Rooftop Area of Hotel The Pride, 267, Rajeev Gandhi Nagar, Near Allen Sankalp, Kota, Rajasthan (324005)';
  const phoneNumber1 = '919024283004';
  const phoneNumber2 = '919602679997';
  const emailAddress = 'info@theporchcafe.in';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    persons: '',
    status: 'pending', // Initialize status for backend compatibility
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(entry.isIntersecting);
        });
      },
      {
        threshold: 0.1,
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const getScrollAnimatedStyle = (delay) => ({
    transform: isVisible ? 'translateY(0)' : 'translateY(50px)',
    opacity: isVisible ? 1 : 0,
    transition: `transform 0.8s ease-out ${delay}s, opacity 0.8s ease-out ${delay}s`,
  });

  const contactContainerStyle = {
    fontFamily: "'Poppins', sans-serif",
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '40px',
    padding: '60px 20px',
    boxSizing: 'border-box',
    marginTop: '5%',
    minHeight: '100vh',
    paddingTop: '0px',
    overflow: 'hidden',
    alignItems: 'center',
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // PDF Download Function - now defined within ContactUs
  const handleDownloadPdf = (dataToPdf, formatDateFunc) => {
    const doc = new jsPDF();

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    let y = 20;

    // Cafe name and address
    doc.setFontSize(18);
    doc.setTextColor(212, 167, 98); // Cafe gold color
    doc.setFont("helvetica", "bold");
    doc.text("The Porch Cafe & Restaurant", pageWidth / 2, y, { align: "center" });

    y += 8;
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    doc.setFont("helvetica", "normal");
    doc.text("Rooftop Area of Hotel The Pride, 267, Rajeev Gandhi Nagar,", pageWidth / 2, y, { align: "center" });
    y += 5;
    doc.text("Near Allen Sankalp, Kota, Rajasthan (324005)", pageWidth / 2, y, { align: "center" });

    y += 10;
    doc.setDrawColor(212, 167, 98); // golden divider
    doc.line(margin, y, pageWidth - margin, y);

    y += 10;
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text("Cafe Booking Details", pageWidth / 2, y, { align: "center" });

    y += 12;

    const detailSection = (title, items) => {
      doc.setTextColor(212, 167, 98);
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text(title, margin, y);
      y += 7;

      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(33, 33, 33);

      items.forEach(({ label, value }) => {
        doc.text(`${label}:`, margin, y);
        doc.text(`${value || '-'}`, margin + 50, y);
        y += 6;
      });

      y += 5;
    };

    detailSection("Guest Information", [
      { label: "Guest Name", value: dataToPdf.name },
      { label: "Email", value: dataToPdf.email },
      { label: "Phone", value: dataToPdf.phone },
      { label: "Number of Persons", value: dataToPdf.persons },
    ]);

    detailSection("Booking Information", [
      { label: "Booking Date", value: formatDateFunc(dataToPdf.date) },
      { label: "Booking Time", value: dataToPdf.time },
      { label: "Booking Status", value: dataToPdf.status }, // Ensure status is passed in receiptData
    ]);

    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text("Thank you for choosing The Porch Cafe & Restaurant!", pageWidth / 2, y + 10, { align: "center" });

    doc.save(`Reservation_${dataToPdf.name || 'Guest'}_${Date.now()}.pdf`);
  };


  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent default form submission behavior

    const { name, email, phone, date, time, persons } = formData;
    if (!name || !email || !phone || !date || !time || persons === '' || persons === null) {
      alert('Please fill in all required fields before reserving.');
      return;
    }

    const parsedPersons = parseInt(persons, 10);
    if (isNaN(parsedPersons) || parsedPersons <= 0) {
      alert('Number of persons must be a positive number.');
      return;
    }

    try {
      const payload = {
        ...formData,
        persons: parsedPersons, // Ensure persons is a number
        status: 'Pending' // Explicitly set status for the backend
      };

      // Ensure this URL is correct for your live backend
      const res = await axios.post('https://admin.theporch.in/api/cafe_reservations', payload, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
  
      if (res.status === 201) { // Check for HTTP 201 Created status
        setReceiptData(payload); // Use the payload for receipt data, which includes status
        setShowReceipt(true);
        // Reset form after successful submission
        setFormData({ name: '', email: '', phone: '', date: '', time: '', persons: '', status: 'pending' });
      } else {
        // This 'else' block would catch 2xx responses that are not 201 and have an unexpected structure.
        alert('Error submitting reservation: ' + (res.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('❌ Error submitting reservation:', error);
      if (error.response) {
        // Server responded with a status other than 2xx (e.g., 400, 500)
        console.error('Error data:', error.response.data);
        console.error('Error status:', error.response.status);
        console.error('Error headers:', error.response.headers);
        alert(`Error submitting reservation: ${error.response.data.error || error.message}. Status: ${error.response.status}`);
      } else if (error.request) {
        // Request was made but no response received (e.g., network error, server down)
        console.error('Error request:', error.request);
        alert('Error submitting reservation: No response from server. Please ensure your backend is running and accessible at https://admin.theporch.in.');
      } else {
        // Something else happened in setting up the request
        alert('Error submitting reservation: ' + error.message);
      }
    }
  };

  // Google Maps URLs
  // The directions URL can still use encodeURIComponent(address) if you wish to provide directions
  const googleMapsDirectionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
  // The embed URL is now directly from your provided string
  const googleMapsEmbedUrl = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3235.7011309951695!2d75.84578947489031!3d25.137535077749465!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x396f85c82fa55981%3A0xe56d0b917178f5a4!2sThe%20Porch%20Cafe%20%26%20Restaurant!5e1!3m2!1sen!2sin!4v1753060005620!5m2!1sen!2sin";
  
  return (
    <>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;700&display=swap');
          @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&display=swap');

          .contact-container {
            font-family: 'Poppins', sans-serif;
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: 40px;
            padding: 60px 20px;
            background-color: #fffdf9;
            box-sizing: border-box;
            margin-top: 5%;
          }

          .contact-left {
            flex: 1 1 300px;
            max-width: 400px;
            color: #111;
            box-sizing: border-box;
            text-align: ${isMobile ? 'center' : 'left'};
            margin: ${isMobile ? '0 auto' : '0'};
          }

          .contact-right {
            flex: 1 1 300px;
            max-width: 500px;
            width: 100%;
            background-color: #fff;
            border: 1px solid #ccc;
            border-radius: 12px;
            padding: 30px;
            box-shadow: 0 0 10px rgba(0,0,0,0.05);
            box-sizing: border-box;
          }

          /* --- Styles for the NEW left section content --- */
          .new-contact-heading {
            font-weight: 600;
            font-size: 18px;
            margin-bottom: 5px;
            color: #D4A762;
            text-transform: uppercase;
            font-family: 'Poppins', sans-serif;
            text-align: inherit;
            line-height: 1.2;
            letter-spacing: -0.2px;
          }

          .new-contact-subtext {
            font-size: 14px;
            line-height: 1.6;
            margin-bottom: 30px;
            color: #222;
            text-align: inherit;
          }

          .new-info-group {
            display: flex;
            flex-direction: column;
            gap: 15px;
          }

          .new-info-item {
            display: flex;
            align-items: center;
            gap: 15px;
            cursor: pointer;
          }

          .new-icon-circle {
            background-color: #D4A762;
            width: 32px;
            height: 32px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: background-color 0.3s ease;
          }

          .new-info-item:hover .new-icon-circle {
            background-color: #c28f13;
          }

          .new-icon {
            color: white;
            font-size: 13px;
          }

          .new-info-label {
            font-weight: 600;
            font-size: 14px;
            color: #111;
            margin-bottom: 0px;
            line-height: 1.2;
          }

          .new-info-value {
            font-size: 13px;
            color: #1a1a1a;
            margin: 0;
            line-height: 1.5;
          }

          .new-email-link {
            color: #1a1a1a;
            text-decoration: none;
          }
          .new-email-link:hover {
            text-decoration: underline;
          }
          /* --- END New Left Section Styles --- */

          .form-group {
            margin-bottom: 16px;
            width: 100%;
            box-sizing: border-box;
          }

          .form-group label {
            font-size: 14px;
            margin-bottom: 6px;
            font-weight: 500;
            display: block;
            color: #333;
          }

          .form-group input {
            width: 100%;
            padding: 12px;
            font-size: 14px;
            border-radius: 6px;
            border: 1px solid #e0e0e0;
            background-color: #fcfcfc;
            height: 45px;
            box-sizing: border-box;
          }

          .form-row {
            display: flex;
            gap: 10px;
            flex-wrap: nowrap;
          }

          .form-half {
            flex: 1;
            min-width: 0;
          }

          .form-third {
            flex: 1;
            min-width: 0;
          }

          .submit-button {
            margin-top: 20px;
            width: 100%;
            padding: 12px;
            font-size: 16px;
            font-weight: 500;
            background-color: #d4a017;
            color: #fff;
            border: none;
            border-radius: 25px;
            cursor: pointer;
            transition: background-color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease;
            position: relative;
            overflow: hidden;
            z-index: 1;
          }

          .submit-button::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 0%;
            height: 100%;
            background-color: #c08b0f;
            transition: width 0.3s ease-out;
            z-index: -1;
          }

          .submit-button:hover::before {
            width: 100%;
          }

          .submit-button:hover {
            color: #fff;
            transform: translateY(-2px);
            box-shadow: 0 4px 10px rgba(0,0,0,0.2);
          }

          @keyframes slideInUp {
            from {
              opacity: 0;
              transform: translateY(50px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          /* Media query for tablet screens (768px and below) */
          @media (max-width: 768px) {
            .contact-container-full-height {
              min-height: auto !important;
              padding-top: 60px !important;
              margin-top: 0% !important;
            }
            .new-contact-heading {
              font-size: 16px;
              margin-bottom: 5px;
            }
            .new-contact-subtext {
              font-size: 13px;
              margin-bottom: 25px;
            }
            .new-info-group {
                gap: 15px;
            }
            .contact-left {
                text-align: center;
                margin: 0 auto;
                max-width: 90%;
            }
            .contact-right {
                max-width: 90%;
                margin: 0 auto;
                padding: 25px;
            }
            .form-row .form-half,
            .form-row .form-third {
                flex: 1;
                min-width: 0;
            }
            .form-row:last-of-type {
                gap: 8px;
            }
            .form-row:last-of-type .form-third {
                width: calc(33.33% - 5.33px);
            }
            .form-group label, .form-group input {
                font-size: 13px;
                padding: 10px;
            }
            .new-info-item {
                flex-direction: column;
                align-items: center;
                text-align: center;
            }
            .new-icon-circle {
                margin-bottom: 5px;
            }
            .new-info-label {
                font-size: 14px;
            }
            .new-info-value {
                font-size: 13px;
            }
          }

          /* Media query for small mobile screens (600px and below) */
          @media (max-width: 600px) {
            .contact-right {
              padding: 20px;
            }
            .form-group {
                margin-bottom: 12px;
            }
            .form-row {
              flex-direction: column;
              gap: 0;
              overflow-x: hidden;
            }

            .form-row .form-half,
            .form-row .form-third {
                width: 100%;
                min-width: 100%;
                max-width: 100%;
                margin-bottom: 0;
            }

            .form-row:last-of-type {
                flex-direction: row;
                flex-wrap: wrap;
                gap: 10px;
                justify-content: space-between;
            }

            .form-row:last-of-type .form-third:nth-child(1),
            .form-row:last-of-type .form-third:nth-child(2) {
                width: calc(50% - 5px);
                min-width: calc(50% - 5px);
                max-width: calc(50% - 5px);
            }

            .form-row:last-of-type .form-third:nth-child(3) {
                width: 100%;
                min-width: 100%;
                max-width: 100%;
                margin-top: 10px;
            }

            .form-group label {
              font-size: 11px;
              margin-bottom: 2px;
            }
            .form-group input {
              height: 35px;
              padding: 8px;
              font-size: 12px;
            }
            .new-info-item {
                flex-direction: column;
                align-items: center;
                text-align: center;
            }
            .new-icon-circle {
                margin-bottom: 5px;
            }
        `}
      </style>

      <SimpleBanner bgImage={aboutBg} headingText="Contact Us" />
      <section id={id} style={contactContainerStyle} className="contact-container-full-height" ref={sectionRef}>
        {/* Left Section (Updated content) */}
        <div className="contact-left">
          <h2 className="new-contact-heading" style={getScrollAnimatedStyle(0.1)}>
            WE’D LOVE TO HEAR FROM YOU!
          </h2>

          <p className="new-contact-subtext" style={getScrollAnimatedStyle(0.3)}>
            Whether it’s a table reservation, event inquiry, or just a quick hello — we’re here and happy to connect.
          </p>

          <div className="new-info-group" style={getScrollAnimatedStyle(0.5)}>
            <div
              className="new-info-item"
              onClick={() => window.open(googleMapsDirectionsUrl, '_blank')}
            >
              <div className="new-icon-circle">
                <MapPin className="new-icon" />
              </div>
              <div>
                <p className="new-info-label">Visit Us:</p>
                <p className="new-info-value">
                  The Porch Cafe & Restaurant, Rooftop Area of Hotel The Pride, 267, Rajeev Gandhi Nagar,<br />
                  Near Allen Sankalp, Kota, Rajasthan (324005)
                </p>
              </div>
            </div>

            <div
              className="new-info-item"
              onClick={() => window.location.href = `tel:+${phoneNumber1}`}
            >
              <div className="new-icon-circle">
                <Phone className="new-icon" />
              </div>
              <div>
                <p className="new-info-label">Call Us:</p>
                <p className="new-info-value">
                  +91 {phoneNumber1.substring(2, 7)} {phoneNumber1.substring(7)}<br />
                  +91 {phoneNumber2.substring(2, 7)} {phoneNumber2.substring(7)}
                </p>
              </div>
            </div>

            <div className="new-info-item">
              <div className="new-icon-circle">
                <Clock className="new-icon" />
              </div>
              <div>
                <p className="new-info-label">Timing:</p>
                <p className="new-info-value">11:00 AM – 11:00 PM</p>
              </div>
            </div>

            <div
              className="new-info-item"
              onClick={() => window.location.href = `mailto:${emailAddress}`}
            >
              <div className="new-icon-circle">
                <Mail className="new-icon" />
              </div>
              <div>
                <p className="new-info-label">Email Us:</p>
                <p className="new-info-value">
                  <a href={`mailto:${emailAddress}`} className="new-email-link">
                    {emailAddress}
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Section (Form) */}
        <div className="contact-right" style={getScrollAnimatedStyle(0.7)}>
          <div className="form-group">
            <label>Your Name</label>
            <input
              type="text"
              placeholder="Enter your name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group form-half">
              <label>Email Address</label>
              <input
                type="email"
                placeholder="Enter email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group form-half">
              <label>Phone Number</label>
              <input
                type="tel"
                placeholder="Enter phone number"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group form-third">
              <label>Date</label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                min={new Date().toISOString().split('T')[0]} // Disable past dates
                required
              />
            </div>
            <div className="form-group form-third">
              <label>Time</label>
              <input
                type="time"
                name="time"
                value={formData.time}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group form-third">
                <label>No. Of Person</label>
                <input
                  type="number"
                  min="1"
                  name="persons"
                  value={formData.persons}
                  onChange={handleChange}
                  placeholder="-"
                  required
                />
            </div>
          </div>

          <button className="submit-button" style={getScrollAnimatedStyle(0.9)} onClick={handleSubmit}>
            Reserve Now
          </button>
        </div>

      </section>
      {/* Embedded Map */}
      <div className="w-full h-[300px] sm:h-[400px] md:h-[500px] mb-6">
      <iframe
        src={googleMapsEmbedUrl} // Updated Google Maps embed URL
        width="100%"
        height="100%"
        style={{ border: 0 }} // Correct way to apply inline style for border
        allowFullScreen=""
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        title="Google Map of The Porch Cafe & Restaurant"
    ></iframe>
      </div>

      {showReceipt && <ReceiptPopup data={receiptData} onClose={() => setShowReceipt(false)} onDownloadPdf={handleDownloadPdf} />}
    </>
  );
};

export default ContactUs;